import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, fmt, fmtDelta } from "../../lib/game";
import { Button } from "../ui/button";
import { Loader2, Gamepad2, ArrowRight, Trophy, Clock } from "lucide-react";

export const MyGames = ({ onOpen, onNew }) => {
  const [games, setGames] = useState(null);

  useEffect(() => {
    api.get("/me/sessions").then(({ data }) => setGames(data)).catch(() => setGames([]));
  }, []);

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-50">My Games</h1>
            <p className="text-slate-400 mt-1">Revisit your past financial journeys.</p>
          </div>
          <Button
            data-testid="new-game-btn"
            onClick={onNew}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold"
          >
            New Game
          </Button>
        </div>

        {games === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-20 rounded-3xl border border-slate-800 bg-slate-900/50">
            <Gamepad2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No games yet. Start your first simulation!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-games-list">
            {games.map((g) => {
              const complete = g.status === "complete";
              const change = g.net_worth - (g.starting_net_worth ?? 0);
              return (
                <motion.button
                  key={g.id}
                  type="button"
                  data-testid={`game-item-${g.id}`}
                  onClick={() => onOpen(g)}
                  whileHover={{ scale: 1.01 }}
                  className="w-full text-left rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-slate-600 px-5 py-4 flex items-center gap-4 transition-colors"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${complete ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-yellow-500/15 border border-yellow-500/30"}`}>
                    {complete ? <Trophy className="w-5 h-5 text-emerald-400" /> : <Clock className="w-5 h-5 text-yellow-400" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-100">
                      <span data-testid={`game-country-${g.id}`} className="mr-2 align-middle font-mono text-[10px] tracking-widest text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5">
                        {g.country || "UK"}
                      </span>
                      {g.nickname}{g.group_name ? ` · ${g.group_name}` : ""}
                    </p>
                    <p className="text-xs text-slate-400">
                      {complete ? `Completed${g.persona ? ` — ${g.persona}` : ""}` : `Round ${g.current_round} of 8`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono font-bold text-slate-100">{fmt(g.net_worth)}</p>
                    {complete && (
                      <p className={`text-xs font-mono ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {fmtDelta(change)}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
